import { z } from "zod";

/**
 * Validación cliente del input de un Consejo. El back valida de nuevo
 * y es la fuente de verdad — este schema es para feedback inmediato
 * en el form, no una medida de seguridad.
 *
 * Reglas:
 *   - `category` es un slug de `AdviceCategory` (requerido).
 *   - `body` 1–400 chars (el límite 400 coincide con el spec del card
 *     "consejo del día" en mobile).
 *   - `position` entero ≥ 0 opcional (el back auto-asigna si falta).
 *   - `active` default true — un consejo nuevo nace visible salvo que
 *     la editora quiera dejarlo en borrador.
 */
export const AdviceInputSchema = z.object({
  category: z.string({ required_error: "Elige una categoría" }).min(1, "Elige una categoría"),
  body: z
    .string({ required_error: "Escribe el consejo" })
    .trim()
    .min(1, "Escribe el consejo")
    .max(400, "Máximo 400 caracteres"),
  position: z
    .number({ invalid_type_error: "Debe ser un número" })
    .int("Debe ser un entero")
    .min(0, "No puede ser negativo")
    .optional(),
  active: z.boolean().default(true),
});

export type AdviceInput = z.infer<typeof AdviceInputSchema>;

/**
 * Schema de categoría.
 *
 * `slug` es la primary key — kebab-case, letras y números ASCII. No se
 * puede editar después de crear (por eso el update del back no lo
 * acepta). El form de edición lo pinta como read-only.
 */
export const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const AdviceCategoryInputSchema = z.object({
  slug: z
    .string({ required_error: "Define el slug" })
    .trim()
    .min(2, "Mínimo 2 caracteres")
    .max(60, "Máximo 60 caracteres")
    .regex(SLUG_REGEX, "Solo minúsculas, números y guiones (ej. salud-mental)"),
  label: z
    .string({ required_error: "Escribe el nombre" })
    .trim()
    .min(1, "Escribe el nombre")
    .max(60, "Máximo 60 caracteres"),
  emoji: z.string().trim().max(8, "Un emoji solo").optional().or(z.literal("")),
  position: z
    .number({ invalid_type_error: "Debe ser un número" })
    .int("Debe ser un entero")
    .min(0, "No puede ser negativo")
    .optional(),
});

export type AdviceCategoryInput = z.infer<typeof AdviceCategoryInputSchema>;

/** Subset de campos editables en el update (slug no incluido). */
export const AdviceCategoryEditSchema = AdviceCategoryInputSchema.omit({ slug: true });
export type AdviceCategoryEdit = z.infer<typeof AdviceCategoryEditSchema>;
